import React, { useMemo } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';

export interface SlotAreaProps {
    type: 'chord' | 'lyrics' | 'empty';
    onPress: () => void;
    isActive: boolean;
    showLyrics?: boolean; // For chord area margin logic
    children: React.ReactNode;
}

const createStyles = (
    theme: Theme,
    type: SlotAreaProps['type'],
    showLyrics: boolean
) => {
    // Spacing between stacked areas within a slot
    const areaSpacing = theme.spacing.xs;
    const slotSizes = theme.sizes.slot;

    // Base styles shared by all areas
    const baseStyle = {
        display: 'flex',
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        borderRadius: theme.radii.md,
        // Asymmetric margins: the slot's separator line sits on its right edge,
        // so the right margin is slightly wider to keep content visually centered
        marginLeft: theme.spacing.xxs,
        marginRight: theme.spacing.xs,
        position: 'relative' as const,
        cursor: 'pointer',
    };

    // Type-specific styles
    let typeSpecificStyle = {};

    switch (type) {
        case 'chord':
            typeSpecificStyle = {
                flex: 1, // Sized by content (degree / name / tones); no fixed minimum
                marginBottom: showLyrics ? areaSpacing : 0, // Spacing to lyrics below
            };
            break;

        case 'lyrics':
            typeSpecificStyle = {
                minHeight: slotSizes.lyricsMinHeight,
            };
            break;

        case 'empty':
            typeSpecificStyle = {
                flex: 1,
                minHeight: slotSizes.chordMinHeight,
                height: '100%',
            };
            break;
    }

    return {
        area: {
            ...baseStyle,
            ...typeSpecificStyle,
        },
        activeArea: {
            backgroundColor: theme.colors.surfaceTint,
        },
    };
};

const SlotArea: React.FC<SlotAreaProps> = ({
    type,
    onPress,
    isActive,
    showLyrics = false,
    children,
}) => {
    const { theme } = useTheme();
    const styles = useMemo(
        () => createStyles(theme, type, showLyrics),
        [theme, type, showLyrics]
    );


    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Let a button inside the area handle its own click
        const target = e.target as HTMLElement;
        if (target.closest('button')) {
            return;
        }

        // The slot's own press selects its row and section too: bubbling to
        // the row and section handlers would toggle them back off
        e.stopPropagation();
        onPress();
    };


    return (
        <div
            style={{
                ...styles.area,
                ...(isActive ? styles.activeArea : {}),
            }}
            onClick={handleClick}
        >
            {children}
        </div>
    );
};

export default React.memo(SlotArea);
